import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { LocalizationService } from '@app/core';
import { UserCvListItem } from '@app/vinter/models/user-cv-list-item';
import { UserManagementService } from '@app/vinter/services/user-management.service';
import * as pdfjsLib from 'pdfjs-dist';
import { jsPDF } from 'jspdf';
import { finalize } from 'rxjs/operators';
import { Subscription, timer } from 'rxjs';

// Backend CVParseAIResultStatus ile birebir.
enum CvAnalysisStatus { Waiting = 1, Successful = 2, Fail = 3, Retry = 4, ClientFail = 5 }

// RESUME tab sağ panelinin olası durumları.
type CvPanelState = 'noCvStep' | 'notAnalyzed' | 'analyzing' | 'failed' | 'scored';

@Component({
    selector: 'app-resume-tab',
    templateUrl: './resume-tab.component.html',
    styleUrls: ['./resume-tab.component.scss']
})

export class ResumeTabComponent implements OnInit, OnDestroy {
    @Input() applicationId: string;
    userCVAndNameInfo: UserCvListItem = null;
    loading: boolean = true;
    triggeringAnalysis: boolean = false;

    // Analiz sürerken sonucu arka planda yokla; sayfa yenilemeden skor gelsin.
    private static readonly POLL_INTERVAL_MS = 10000;
    private static readonly POLL_MAX_MS = 10 * 60 * 1000;
    private pollSub: Subscription;
    private pollStartedAt: number;

    constructor(private userManagementService: UserManagementService, private translate: LocalizationService) {
    }

    // Sağ panel durumu:
    //  - noCvStep    : pozisyonda CV adımı yok → değerlendirme yapılmaz, sadece bilgi notu
    //  - notAnalyzed : CV adımı var ama bu pozisyon için parse kaydı yok (aday davetle geldi) → "mevcut CV'yi analiz et"
    //  - analyzing   : kayıt Waiting/Retry, ya da Successful ama skor callback'i henüz yazılmadı
    //  - failed      : Fail/ClientFail
    //  - scored      : Successful + skor var
    get cvPanelState(): CvPanelState {
        const info = this.userCVAndNameInfo;
        if (!info?.hasCvStep) return 'noCvStep';
        const status = info.cvAnalysisStatus;
        if (status == null) return 'notAnalyzed';
        if (status === CvAnalysisStatus.Fail || status === CvAnalysisStatus.ClientFail) return 'failed';
        if (status === CvAnalysisStatus.Successful && info.cvScore != null) return 'scored';
        return 'analyzing';
    }

    // Faz 4 — CV skor rozeti: eşiğe göre etiket ve renk (GA sidebar ile aynı mantık).
    get cvMatchLabel(): string {
        const score = this.userCVAndNameInfo?.cvScore;
        const threshold = this.userCVAndNameInfo?.cvScoreThreshold;
        if (threshold == null) return this.translate.translateText('CvScoringMatchNoThreshold');
        if (score == null) return '—';
        if (score >= threshold + 20) return this.translate.translateText('CvScoringMatchStrong');
        if (score >= threshold) return this.translate.translateText('CvScoringMatchAbove');
        return this.translate.translateText('CvScoringMatchBelow');
    }

    get cvMatchClass(): string {
        const score = this.userCVAndNameInfo?.cvScore;
        const threshold = this.userCVAndNameInfo?.cvScoreThreshold;
        if (threshold == null || score == null) return 'match-neutral';
        if (score >= threshold + 20) return 'match-strong';
        if (score >= threshold) return 'match-ok';
        return 'match-below';
    }

    ngOnInit(): void {
        this.loading = true;
        this.userManagementService.getUserCvFile(this.applicationId)
            .pipe(
                finalize(() => this.loading = false)
            )
            .subscribe({
                next: (response) => {
                    this.userCVAndNameInfo = response;
                    this.syncPolling();
                },
                error: (error) => {
                    console.error('Error fetching CV file:', error);
                }
            });
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    // Davetle gelen adayın mevcut CV'sini bu pozisyonun CV step kriterleriyle analiz ettir (ya da başarısız analizi tekrar dene).
    triggerCvAnalysis(): void {
        if (this.triggeringAnalysis) return;
        this.triggeringAnalysis = true;
        this.userManagementService.triggerCvAnalysisForApplication(this.applicationId)
            .pipe(finalize(() => this.triggeringAnalysis = false))
            .subscribe({
                next: () => {
                    // Backend kaydı Waiting açtı; sunucu cevabını beklemeden "analiz ediliyor" göster ve yoklamaya başla.
                    this.userCVAndNameInfo = {
                        ...this.userCVAndNameInfo,
                        cvAnalysisStatus: CvAnalysisStatus.Waiting,
                        cvScore: null,
                        cvScoreReasoning: null
                    };
                    this.syncPolling();
                },
                error: () => { /* hata toast'ı HttpBaseService tarafından gösterilir */ }
            });
    }

    private syncPolling(): void {
        if (this.cvPanelState === 'analyzing') this.startPolling();
        else this.stopPolling();
    }

    private startPolling(): void {
        if (this.pollSub) return;
        this.pollStartedAt = Date.now();
        this.pollSub = timer(ResumeTabComponent.POLL_INTERVAL_MS, ResumeTabComponent.POLL_INTERVAL_MS).subscribe(() => {
            if (Date.now() - this.pollStartedAt > ResumeTabComponent.POLL_MAX_MS) {
                this.stopPolling();
                return;
            }
            this.userManagementService.getUserCvFileSilent(this.applicationId).subscribe({
                next: (response) => {
                    if (!response) return;
                    this.userCVAndNameInfo = response;
                    this.syncPolling();
                },
                error: () => { /* geçici hata — bir sonraki tick'te tekrar denenir */ }
            });
        });
    }

    private stopPolling(): void {
        if (this.pollSub) {
            this.pollSub.unsubscribe();
            this.pollSub = null;
        }
    }

    get formattedCvFileName(): string {
        return `${this.userCVAndNameInfo.firstName}_${this.userCVAndNameInfo.lastName}_Cv.pdf`;
    }


    pdf2Image2Pdf(userCvFileUrl: string) {
        if (userCvFileUrl != null) {
            const loadingTask = pdfjsLib.getDocument(userCvFileUrl);

            loadingTask.promise.then((pdf) => {
                const screenWidth = window.innerWidth;
                const screenHeight = window.innerHeight;

                const doc = new jsPDF();

                const totalPages = pdf.numPages;

                const processPage = (pageNum) => {
                    pdf.getPage(pageNum).then((page) => {
                        const viewport = page.getViewport({ scale: 1 });
                        const scale = Math.min(screenWidth / viewport.width, screenHeight / viewport.height);
                        const scaledViewport = page.getViewport({ scale });

                        const canvas = document.createElement('canvas');
                        const context = canvas.getContext('2d');
                        const higherResolutionScale = 2;
                        canvas.width = scaledViewport.width * higherResolutionScale;
                        canvas.height = scaledViewport.height * higherResolutionScale;

                        context.scale(higherResolutionScale, higherResolutionScale);

                        page.render({
                            canvasContext: context,
                            viewport: scaledViewport
                        }).promise.then(() => {
                            const imageData = canvas.toDataURL('image/png');

                            const pageWidth = doc.internal.pageSize.width;
                            const pageHeight = doc.internal.pageSize.height;
                            const imageWidth = canvas.width / 4;
                            const imageHeight = canvas.height / 4;

                            const scaleFactor = Math.min(pageWidth / imageWidth, pageHeight / imageHeight);
                            const finalImageWidth = imageWidth * scaleFactor;
                            const finalImageHeight = imageHeight * scaleFactor;

                            if (pageNum === 1) {
                                doc.addImage(imageData, 'PNG', 0, 0, finalImageWidth, finalImageHeight);
                            } else {
                                doc.addPage();
                                doc.addImage(imageData, 'PNG', 0, 0, finalImageWidth, finalImageHeight);
                            }

                            if (pageNum === totalPages) {
                                doc.save(this.formattedCvFileName);
                            }
                        });
                    });
                };

                for (let i = 1; i <= totalPages; i++) {
                    processPage(i);
                }

            }).catch((error) => {
                console.error('Error processing PDF:', error);
            });
        }
    }

downloadOriginalPdf(userCvFileUrl: string) {
    if (!userCvFileUrl) return;
    fetch(userCvFileUrl)
        .then(res => res.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = this.formattedCvFileName; 
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        })
        .catch(err => console.error('PDF download failed:', err));
    }

}
